import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBetSlip } from '../../../components/feature/BetSlipContext';
import PageHero from '../../../components/feature/PageHero';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FuturesOption {
  name: string;
  prob: string;
  price: string;
  payout: string;
  profit: string;
}

interface FuturesCard {
  badge: string;
  badgeColor: string;
  sport: string;
  title: string;
  volume24h: string;
  totalVolume: string;
  options: FuturesOption[];
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const NFL_FUTURES: FuturesCard[] = [
  {
    badge: 'NFL',
    badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    sport: 'Championship',
    title: 'Super Bowl LX Champion',
    volume24h: '$487.3K',
    totalVolume: '$4.2M',
    options: [
      { name: 'Kansas City Chiefs', prob: '24%', price: '$0.24', payout: '$4.17', profit: '+$3.17' },
      { name: 'San Francisco 49ers', prob: '19%', price: '$0.19', payout: '$5.26', profit: '+$4.26' },
      { name: 'Baltimore Ravens', prob: '16%', price: '$0.16', payout: '$6.25', profit: '+$5.25' },
      { name: 'Philadelphia Eagles', prob: '13%', price: '$0.13', payout: '$7.69', profit: '+$6.69' },
      { name: 'Buffalo Bills', prob: '10%', price: '$0.10', payout: '$10.00', profit: '+$9.00' },
      { name: 'Other', prob: '18%', price: '$0.18', payout: '$5.56', profit: '+$4.56' },
    ],
  },
  {
    badge: 'NFL',
    badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    sport: 'Season Award',
    title: 'NFL MVP 2025-26',
    volume24h: '$321.8K',
    totalVolume: '$2.4M',
    options: [
      { name: 'Lamar Jackson (BAL)', prob: '44%', price: '$0.44', payout: '$2.27', profit: '+$1.27' },
      { name: 'Patrick Mahomes (KC)', prob: '31%', price: '$0.31', payout: '$3.23', profit: '+$2.23' },
      { name: 'Josh Allen (BUF)', prob: '14%', price: '$0.14', payout: '$7.14', profit: '+$6.14' },
      { name: 'Jalen Hurts (PHI)', prob: '6%', price: '$0.06', payout: '$16.67', profit: '+$15.67' },
      { name: 'Other', prob: '5%', price: '$0.05', payout: '$20.00', profit: '+$19.00' },
    ],
  },
  {
    badge: 'NFL',
    badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    sport: 'Conference',
    title: 'AFC Champion',
    volume24h: '$189.7K',
    totalVolume: '$1.3M',
    options: [
      { name: 'Kansas City Chiefs', prob: '38%', price: '$0.38', payout: '$2.63', profit: '+$1.63' },
      { name: 'Baltimore Ravens', prob: '27%', price: '$0.27', payout: '$3.70', profit: '+$2.70' },
      { name: 'Buffalo Bills', prob: '19%', price: '$0.19', payout: '$5.26', profit: '+$4.26' },
      { name: 'Other', prob: '16%', price: '$0.16', payout: '$6.25', profit: '+$5.25' },
    ],
  },
  {
    badge: 'NFL',
    badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    sport: 'Conference',
    title: 'NFC Champion',
    volume24h: '$167.4K',
    totalVolume: '$1.1M',
    options: [
      { name: 'San Francisco 49ers', prob: '34%', price: '$0.34', payout: '$2.94', profit: '+$1.94' },
      { name: 'Philadelphia Eagles', prob: '28%', price: '$0.28', payout: '$3.57', profit: '+$2.57' },
      { name: 'Dallas Cowboys', prob: '19%', price: '$0.19', payout: '$5.26', profit: '+$4.26' },
      { name: 'Other', prob: '19%', price: '$0.19', payout: '$5.26', profit: '+$4.26' },
    ],
  },
];

const NBA_FUTURES: FuturesCard[] = [
  {
    badge: 'NBA',
    badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    sport: 'Championship',
    title: 'NBA Champion 2025-26',
    volume24h: '$412.7K',
    totalVolume: '$3.2M',
    options: [
      { name: 'Boston Celtics', prob: '31%', price: '$0.31', payout: '$3.23', profit: '+$2.23' },
      { name: 'Oklahoma City Thunder', prob: '22%', price: '$0.22', payout: '$4.55', profit: '+$3.55' },
      { name: 'Denver Nuggets', prob: '18%', price: '$0.18', payout: '$5.56', profit: '+$4.56' },
      { name: 'Cleveland Cavaliers', prob: '12%', price: '$0.12', payout: '$8.33', profit: '+$7.33' },
      { name: 'New York Knicks', prob: '8%', price: '$0.08', payout: '$12.50', profit: '+$11.50' },
      { name: 'Other', prob: '9%', price: '$0.09', payout: '$11.11', profit: '+$10.11' },
    ],
  },
  {
    badge: 'NBA',
    badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    sport: 'Season Award',
    title: 'NBA MVP 2025-26',
    volume24h: '$287.6K',
    totalVolume: '$2.1M',
    options: [
      { name: 'Nikola Jokic (DEN)', prob: '39%', price: '$0.39', payout: '$2.56', profit: '+$1.56' },
      { name: 'Shai Gilgeous-Alexander (OKC)', prob: '28%', price: '$0.28', payout: '$3.57', profit: '+$2.57' },
      { name: 'Giannis Antetokounmpo (MIL)', prob: '17%', price: '$0.17', payout: '$5.88', profit: '+$4.88' },
      { name: 'Luka Doncic (DAL)', prob: '10%', price: '$0.10', payout: '$10.00', profit: '+$9.00' },
      { name: 'Other', prob: '6%', price: '$0.06', payout: '$16.67', profit: '+$15.67' },
    ],
  },
  {
    badge: 'NBA',
    badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    sport: 'Conference',
    title: 'Eastern Conference Champion',
    volume24h: '$198.4K',
    totalVolume: '$1.4M',
    options: [
      { name: 'Boston Celtics', prob: '42%', price: '$0.42', payout: '$2.38', profit: '+$1.38' },
      { name: 'Cleveland Cavaliers', prob: '24%', price: '$0.24', payout: '$4.17', profit: '+$3.17' },
      { name: 'New York Knicks', prob: '18%', price: '$0.18', payout: '$5.56', profit: '+$4.56' },
      { name: 'Other', prob: '16%', price: '$0.16', payout: '$6.25', profit: '+$5.25' },
    ],
  },
  {
    badge: 'NBA',
    badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    sport: 'Conference',
    title: 'Western Conference Champion',
    volume24h: '$176.2K',
    totalVolume: '$1.2M',
    options: [
      { name: 'Oklahoma City Thunder', prob: '36%', price: '$0.36', payout: '$2.78', profit: '+$1.78' },
      { name: 'Denver Nuggets', prob: '29%', price: '$0.29', payout: '$3.45', profit: '+$2.45' },
      { name: 'Los Angeles Lakers', prob: '18%', price: '$0.18', payout: '$5.56', profit: '+$4.56' },
      { name: 'Other', prob: '17%', price: '$0.17', payout: '$5.88', profit: '+$4.88' },
    ],
  },
];

const NHL_FUTURES: FuturesCard[] = [
  {
    badge: 'NHL',
    badgeColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    sport: 'Stanley Cup',
    title: 'Stanley Cup Champion 2026',
    volume24h: '$198.4K',
    totalVolume: '$1.7M',
    options: [
      { name: 'Florida Panthers', prob: '24%', price: '$0.24', payout: '$4.17', profit: '+$3.17' },
      { name: 'Colorado Avalanche', prob: '19%', price: '$0.19', payout: '$5.26', profit: '+$4.26' },
      { name: 'Edmonton Oilers', prob: '16%', price: '$0.16', payout: '$6.25', profit: '+$5.25' },
      { name: 'Carolina Hurricanes', prob: '13%', price: '$0.13', payout: '$7.69', profit: '+$6.69' },
      { name: 'Other', prob: '28%', price: '$0.28', payout: '$3.57', profit: '+$2.57' },
    ],
  },
  {
    badge: 'NHL',
    badgeColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    sport: 'Season Award',
    title: 'Hart Trophy 2025-26',
    volume24h: '$143.7K',
    totalVolume: '$812K',
    options: [
      { name: 'Connor McDavid (EDM)', prob: '48%', price: '$0.48', payout: '$2.08', profit: '+$1.08' },
      { name: 'Nathan MacKinnon (COL)', prob: '29%', price: '$0.29', payout: '$3.45', profit: '+$2.45' },
      { name: 'Auston Matthews (TOR)', prob: '14%', price: '$0.14', payout: '$7.14', profit: '+$6.14' },
      { name: 'Other', prob: '9%', price: '$0.09', payout: '$11.11', profit: '+$10.11' },
    ],
  },
  {
    badge: 'NHL',
    badgeColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    sport: 'Division',
    title: 'Atlantic Division Winner',
    volume24h: '$87.6K',
    totalVolume: '$423K',
    options: [
      { name: 'Florida Panthers', prob: '38%', price: '$0.38', payout: '$2.63', profit: '+$1.63' },
      { name: 'Toronto Maple Leafs', prob: '29%', price: '$0.29', payout: '$3.45', profit: '+$2.45' },
      { name: 'Tampa Bay Lightning', prob: '21%', price: '$0.21', payout: '$4.76', profit: '+$3.76' },
      { name: 'Other', prob: '12%', price: '$0.12', payout: '$8.33', profit: '+$7.33' },
    ],
  },
  {
    badge: 'NHL',
    badgeColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    sport: 'Division',
    title: 'Metropolitan Division Winner',
    volume24h: '$76.3K',
    totalVolume: '$387K',
    options: [
      { name: 'Carolina Hurricanes', prob: '41%', price: '$0.41', payout: '$2.44', profit: '+$1.44' },
      { name: 'New York Rangers', prob: '32%', price: '$0.32', payout: '$3.13', profit: '+$2.13' },
      { name: 'New Jersey Devils', prob: '18%', price: '$0.18', payout: '$5.56', profit: '+$4.56' },
      { name: 'Other', prob: '9%', price: '$0.09', payout: '$11.11', profit: '+$10.11' },
    ],
  },
];

const MLB_FUTURES: FuturesCard[] = [
  {
    badge: 'MLB',
    badgeColor: 'bg-red-500/20 text-red-400 border-red-500/30',
    sport: 'World Series',
    title: 'World Series Champion 2026',
    volume24h: '$312.4K',
    totalVolume: '$2.8M',
    options: [
      { name: 'Los Angeles Dodgers', prob: '28%', price: '$0.28', payout: '$3.57', profit: '+$2.57' },
      { name: 'New York Yankees', prob: '19%', price: '$0.19', payout: '$5.26', profit: '+$4.26' },
      { name: 'Atlanta Braves', prob: '14%', price: '$0.14', payout: '$7.14', profit: '+$6.14' },
      { name: 'Houston Astros', prob: '11%', price: '$0.11', payout: '$9.09', profit: '+$8.09' },
      { name: 'Philadelphia Phillies', prob: '8%', price: '$0.08', payout: '$12.50', profit: '+$11.50' },
      { name: 'Other', prob: '20%', price: '$0.20', payout: '$5.00', profit: '+$4.00' },
    ],
  },
  {
    badge: 'MLB',
    badgeColor: 'bg-red-500/20 text-red-400 border-red-500/30',
    sport: 'Season Award',
    title: 'NL Cy Young Award 2026',
    volume24h: '$98.7K',
    totalVolume: '$487K',
    options: [
      { name: 'Zack Wheeler (PHI)', prob: '34%', price: '$0.34', payout: '$2.94', profit: '+$1.94' },
      { name: 'Spencer Strider (ATL)', prob: '27%', price: '$0.27', payout: '$3.70', profit: '+$2.70' },
      { name: 'Logan Webb (SF)', prob: '21%', price: '$0.21', payout: '$4.76', profit: '+$3.76' },
      { name: 'Other', prob: '18%', price: '$0.18', payout: '$5.56', profit: '+$4.56' },
    ],
  },
  {
    badge: 'MLB',
    badgeColor: 'bg-red-500/20 text-red-400 border-red-500/30',
    sport: 'Division',
    title: 'AL East Division Winner',
    volume24h: '$112.8K',
    totalVolume: '$623K',
    options: [
      { name: 'New York Yankees', prob: '41%', price: '$0.41', payout: '$2.44', profit: '+$1.44' },
      { name: 'Baltimore Orioles', prob: '33%', price: '$0.33', payout: '$3.03', profit: '+$2.03' },
      { name: 'Tampa Bay Rays', prob: '16%', price: '$0.16', payout: '$6.25', profit: '+$5.25' },
      { name: 'Other', prob: '10%', price: '$0.10', payout: '$10.00', profit: '+$9.00' },
    ],
  },
  {
    badge: 'MLB',
    badgeColor: 'bg-red-500/20 text-red-400 border-red-500/30',
    sport: 'Season Award',
    title: 'AL MVP Award 2026',
    volume24h: '$134.2K',
    totalVolume: '$743K',
    options: [
      { name: 'Aaron Judge (NYY)', prob: '38%', price: '$0.38', payout: '$2.63', profit: '+$1.63' },
      { name: 'Gunnar Henderson (BAL)', prob: '26%', price: '$0.26', payout: '$3.85', profit: '+$2.85' },
      { name: 'Yordan Alvarez (HOU)', prob: '19%', price: '$0.19', payout: '$5.26', profit: '+$4.26' },
      { name: 'Other', prob: '17%', price: '$0.17', payout: '$5.88', profit: '+$4.88' },
    ],
  },
];

const ALL_FUTURES: FuturesCard[] = [...NFL_FUTURES, ...NBA_FUTURES, ...NHL_FUTURES, ...MLB_FUTURES];

const SPORT_TABS = [
  { key: 'all', label: 'All', icon: 'ri-apps-2-line', data: ALL_FUTURES },
  { key: 'nfl', label: 'NFL', icon: 'ri-football-line', data: NFL_FUTURES },
  { key: 'nba', label: 'NBA', icon: 'ri-basketball-line', data: NBA_FUTURES },
  { key: 'nhl', label: 'NHL', icon: 'ri-snowy-line', data: NHL_FUTURES },
  { key: 'mlb', label: 'MLB', icon: 'ri-trophy-line', data: MLB_FUTURES },
];

const SUBNAV = [
  { key: 'all', label: 'All', icon: 'ri-apps-2-line', href: '/markets' },
  { key: 'spreads', label: 'Spreads', icon: 'ri-bar-chart-grouped-line', href: '/markets/spreads' },
  { key: 'money-lines', label: 'Money Lines', icon: 'ri-copper-coin-line', href: '/markets/money-lines' },
  { key: 'futures', label: 'Futures', icon: 'ri-calendar-event-line', href: '/markets/futures' },
  { key: 'parlays', label: 'Parlays', icon: 'ri-stack-line', href: '/markets/parlays' },
  { key: 'player-props', label: 'Player Props', icon: 'ri-user-star-line', href: '/markets/player-props' },
];

const MARKET_SUB_LINKS = [
  { href: '/markets/spreads', icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
  { href: '/markets/money-lines', icon: 'ri-copper-coin-line', label: 'Money Lines' },
  { href: '/markets/futures', icon: 'ri-calendar-event-line', label: 'Futures' },
  { href: '/markets/parlays', icon: 'ri-stack-line', label: 'Parlays' },
  { href: '/markets/player-props', icon: 'ri-user-star-line', label: 'Player Props' },
];

// ─── Card Component ───────────────────────────────────────────────────────────

function FuturesCardUI({ card }: { card: FuturesCard }) {
  const [selectedOption, setSelectedOption] = useState(card.options[0].name);
  const [added, setAdded] = useState(false);
  const { addBet } = useBetSlip();

  const selected = card.options.find((o) => o.name === selectedOption) ?? card.options[0];

  const handleAdd = () => {
    addBet({
      id: `futures-${card.title}-${selected.name}`.replace(/\s+/g, '-').toLowerCase(),
      market: `${card.badge} Futures — ${card.sport}`,
      matchup: card.title,
      pick: selected.name,
      price: '$1.00',
      payout: selected.payout,
      profit: selected.profit,
      badge: card.badge,
      badgeColor: card.badgeColor,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div
      className="rounded-xl border border-slate-700/60 overflow-hidden flex flex-col"
      style={{ background: '#1a2235' }}
    >
      {/* Card Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700/50"
        style={{ background: '#151e2e' }}
      >
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${card.badgeColor}`}>{card.badge}</span>
          <span className="text-xs text-slate-400 font-medium">{card.sport}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <i className="ri-line-chart-line text-teal-500"></i>
          <span className="text-teal-500 font-semibold">{card.volume24h}</span>
          <span>24h</span>
        </div>
      </div>

      {/* Title */}
      <div className="px-4 pt-3 pb-2">
        <h3 className="text-sm font-bold text-white leading-tight">{card.title}</h3>
        <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
          <i className="ri-water-flash-line text-emerald-400"></i>
          <span>Total Vol: <span className="text-emerald-400 font-semibold">{card.totalVolume}</span></span>
        </div>
      </div>

      {/* Options List */}
      <div className="px-4 pb-3 flex-1">
        <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-0.5">
          {card.options.map((option, idx) => (
            <div
              key={idx}
              onClick={() => setSelectedOption(option.name)}
              className={`cursor-pointer px-3 py-2 rounded-lg border transition-all flex items-center justify-between ${
                option.name === selectedOption
                  ? 'bg-teal-500/10 border-teal-500/50'
                  : 'bg-white/5 border-slate-700/60 hover:border-slate-500/60'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div
                  className={`text-xs font-semibold truncate ${
                    option.name === selectedOption ? 'text-teal-300' : 'text-slate-300'
                  }`}
                >
                  {option.name}
                </div>
                <div className="text-xs text-slate-500">Prob: {option.prob}</div>
              </div>
              <div className="text-right pl-2 flex-shrink-0">
                <div className="text-xs text-slate-400">Bet: $1.00</div>
                <div className="text-xs text-teal-400 font-semibold">{option.profit}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Summary */}
      <div className="px-4 py-2 border-t border-slate-700/40 flex items-center justify-between text-xs">
        <span className="text-slate-500">Bet $1.00 on <span className="text-slate-300 font-medium truncate max-w-[120px] inline-block align-bottom">{selected.name}</span></span>
        <span className="text-teal-400 font-bold">Win {selected.payout}</span>
      </div>

      {/* Add to Slip */}
      <div className="px-4 pb-4 pt-1">
        <button
          onClick={handleAdd}
          className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
            added
              ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
              : 'bg-teal-500/10 border-teal-500/30 text-teal-400 hover:bg-teal-500/20'
          }`}
        >
          <i className={`text-sm ${added ? 'ri-check-line' : 'ri-add-circle-line'}`}></i>
          {added ? 'Added!' : 'Add to Slip'}
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MarketsFuturesPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);
  const { items: slipItems, toggleSlip } = useBetSlip();

  const tabParam = searchParams.get('tab') || 'all';
  const activeTab = SPORT_TABS.find((t) => t.key === tabParam) ?? SPORT_TABS[0];

  const handleTabChange = (key: string) => {
    navigate(`/markets/futures?tab=${key}`);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-futures';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Futures Markets — L2L Sports Prediction Market',
      description: 'Trade championship, MVP, and season-long futures markets for NFL, NBA, NHL, and MLB. Zero juice futures betting.',
      url: `${siteUrl}/markets/futures`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Markets', item: `${siteUrl}/markets` },
          { '@type': 'ListItem', position: 3, name: 'Futures', item: `${siteUrl}/markets/futures` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#0f1623' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 relative z-[100]">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-base"></i>
          <span className="font-bold text-xs md:text-sm tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(15,22,35,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png" alt="RTJ Logo" className="h-9 w-auto object-contain" />
              </div>
            </a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>Home</div></a>
              <a href="/march-madness"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>March Madness</div></a>
              <a href="/super-bowl-props"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>Super Bowl</div></a>
              <div className="relative" ref={marketsDropdownRef}>
                <button
                  onClick={() => setMarketsDropdownOpen((o) => !o)}
                  className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400 transition-all"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50"
                    style={{ background: 'rgba(15,22,35,0.99)' }}
                  >
                    {MARKET_SUB_LINKS.map((link) => (
                      <a key={link.href} href={link.href} onClick={() => setMarketsDropdownOpen(false)}>
                        <div
                          className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                            link.href === '/markets/futures'
                              ? 'bg-teal-500/10 text-teal-400'
                              : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
                          }`}
                        >
                          <i
                            className={`${link.icon} w-4 h-4 flex items-center justify-center ${
                              link.href === '/markets/futures' ? 'text-teal-400' : 'text-slate-500'
                            }`}
                          ></i>
                          {link.label}
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className="ri-apps-2-line w-4 h-4 flex items-center justify-center text-slate-500"></i>
                          All Markets
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <a href="/why-rtj">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>
                  Why L2L
                </div>
              </a>
              <a href="/my-account"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>My Account</div></a>
            </div>

            {/* Right Icons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-teal-500/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button
                onClick={toggleSlip}
                className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer"
              >
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                {slipItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{slipItems.length}</span>
                )}
              </button>
              <a href="/my-account">
                <div className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer transition-colors">
                  <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {[
                { href: '/', icon: 'ri-home-line', label: 'Home' },
                { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
                { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
                { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'All Markets' },
                { href: '/markets/spreads', icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
                { href: '/markets/money-lines', icon: 'ri-copper-coin-line', label: 'Money Lines' },
                { href: '/markets/futures', icon: 'ri-calendar-event-line', label: 'Futures' },
                { href: '/markets/parlays', icon: 'ri-stack-line', label: 'Parlays' },
                { href: '/markets/player-props', icon: 'ri-user-star-line', label: 'Player Props' },
                { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
                { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
              ].map((item) => (
                <a key={item.href} href={item.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${item.icon} text-base`}></i>
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        title="Futures Markets"
        subtitle="Trade championships, MVP awards & season-long outcomes with zero juice"
        badgeLabel="Season Long"
        badgeColor="bg-amber-500/20 border-amber-500/40 text-amber-400"
        backgroundImage="https://public.readdy.ai/ai/img_res/bdfe0667b4fd4dffe406e5bf8b40ed44.jpg"
      />

      {/* Sub Navigation */}
      <div
        className="sticky top-[57px] z-40 border-b border-slate-700/50"
        style={{ background: 'rgba(15,22,35,0.98)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-2">
            {SUBNAV.map((sub) => (
              <a key={sub.key} href={sub.href}>
                <div
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                    sub.key === 'futures'
                      ? 'bg-slate-700 text-white border border-slate-500/60'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                  }`}
                >
                  <i
                    className={`${sub.icon} text-sm ${
                      sub.key === 'futures' ? 'text-teal-400' : 'text-slate-500'
                    }`}
                  ></i>
                  {sub.label}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Sport Filter Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          {SPORT_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                activeTab.key === tab.key
                  ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/25'
                  : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">
            <i className="ri-trophy-line text-yellow-500"></i>
            <span>{activeTab.data.length} markets</span>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {activeTab.data.map((card, idx) => (
            <FuturesCardUI key={idx} card={card} />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer
        className="border-t border-slate-700/50 py-8 px-4 mt-8"
        style={{ background: '#0a1020' }}
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="RTJ Logo"
              className="h-8 w-auto object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">
            Demo only. Not real money. For educational purposes only. Must be 18+ to participate.
          </p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 transition-colors cursor-pointer">Super Bowl</a>
            <a href="/march-madness" className="hover:text-slate-300 transition-colors cursor-pointer">March Madness</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
